import React, { useMemo, useState } from 'react';
import { CheckCircle2, Loader2, Lock, Trash2, UserCog } from 'lucide-react';
import {
  useBrokerRoster,
  matchAdviserKey,
  type RosterDisposition,
} from '../../../hooks/pipelineTracker/useBrokerRoster';
import type { AdviserOption } from '../../../hooks/pipelineTracker/usePipelineReport';
import { num } from './pipelineParts';
import { hexRgb } from './trackerKit';
import { useSectionAccent } from './TrackerSection';

/** The box's accent outside a TrackerSection; inside one it wears the section's colour. */
const ROSTER_ACCENT = '#8b5cf6';

/** The house panel accent (trackerKit HousePanel) on a box that sits inside a Pipeline panel. */
const RosterBox: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const accent = useSectionAccent() ?? ROSTER_ACCENT;
  const rgb = hexRgb(accent);
  return (
    <div
      data-panel-accent={accent}
      data-panel-title="Who is a broker"
      className="relative overflow-hidden rounded-xl border p-4 pt-5"
      style={{
        borderColor: `rgba(${rgb},0.3)`,
        background: `linear-gradient(180deg, rgba(${rgb},0.08), rgba(31,41,55,0.4))`,
      }}
    >
      <div aria-hidden="true" className="absolute inset-x-0 top-0 h-[0.1875rem]" style={{ background: accent }} />
      {children}
    </div>
  );
};

/** The roster heading's icon chip, in the box's colour. */
const RosterIcon: React.FC = () => {
  const accent = useSectionAccent() ?? ROSTER_ACCENT;
  return (
    <span
      aria-hidden="true"
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md"
      style={{ background: `rgba(${hexRgb(accent)},0.18)`, color: accent }}
    >
      <UserCog className="h-4 w-4" />
    </span>
  );
};

/**
 * Saying who is a broker, who is support, and which book somebody came with.
 *
 * WHY THE PRACTICE HAS TO SAY. Nothing in a Salestrekker export marks a role,
 * and the demos this week showed every SFG practice organising itself
 * differently: support staff owning deal cards, acquired books still under the
 * original brokers' names, credit analysts holding files mid-assessment. There
 * is no rule the product could infer without being wrong for somebody, so this
 * is stated once and obeyed.
 *
 * WHY IT IS INLINE, LIKE THE STAGE MAPPING ABOVE IT. The decision needs the
 * deal counts next to it. Someone holding 1,690 deals is obviously a broker and
 * someone holding 1 obviously needs a look, and that is the whole judgement.
 * Sending an admin to a separate settings page would mean deciding without the
 * one number that makes it easy.
 *
 * WHAT SAVING DOES AND DOES NOT DO. It changes how this page ARRANGES people.
 * It never changes a figure: a support user's deals stay in every company total
 * before and after, which is why the page says so out loud on the support row
 * rather than leaving a reader to assume the totals moved.
 */

export interface BrokerRosterPanelProps {
  companyId: string;
  /** Caller's role. Only super_admin and administrator may edit, mirroring TAXONOMY_ROLES. */
  canEdit: boolean;
  /** Everyone in the snapshot, unfiltered, so a person with no deals in scope is still listed. */
  advisers: AdviserOption[];
  /** Called after a successful save so the report re-reads with the new roster. */
  onSaved: () => void;
}

const adviserKeyOf = (adviser: AdviserOption): string =>
  (adviser.adviserId ?? '').trim() !== '' ? (adviser.adviserId as string) : adviser.adviserName.trim();

const BrokerRosterPanel: React.FC<BrokerRosterPanelProps> = ({
  companyId,
  canEdit,
  advisers,
  onSaved,
}) => {
  const roster = useBrokerRoster(companyId, canEdit);
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState<Record<string, true>>({});
  const [newBook, setNewBook] = useState('');

  // Busiest first: the people whose classification matters most are the ones
  // carrying the most deals, and they should not be below a name with one.
  const people = useMemo(
    () => [...advisers].sort((a, b) => b.totalCount - a.totalCount),
    [advisers]
  );

  const unclassified = useMemo(
    () => people.filter((person) => !roster.byKey.has(matchAdviserKey(adviserKeyOf(person)))).length,
    [people, roster.byKey]
  );

  const apply = async (
    person: AdviserOption,
    disposition: RosterDisposition | 'unclassified',
    groupKey?: string
  ) => {
    setProblem(null);
    const key = adviserKeyOf(person);
    const outcome =
      disposition === 'unclassified'
        ? await roster.clearDisposition(key)
        : await roster.setDisposition(key, person.adviserName, disposition, groupKey);

    if (outcome.ok) {
      setSaved((prev) => ({ ...prev, [key]: true }));
      onSaved();
    } else {
      setProblem(outcome.message ?? 'That change could not be saved.');
    }
  };

  const addBook = async () => {
    setProblem(null);
    const outcome = await roster.addGroup(newBook);
    if (outcome.ok) {
      setNewBook('');
      onSaved();
    } else {
      setProblem(outcome.message ?? 'That book could not be added.');
    }
  };

  const removeBook = async (groupKey: string) => {
    setProblem(null);
    const outcome = await roster.removeGroup(groupKey);
    if (outcome.ok) onSaved();
    else setProblem(outcome.message ?? 'That book could not be removed.');
  };

  if (!canEdit) {
    return (
      <RosterBox>
        <span className="flex items-center gap-1.5 text-xs text-gray-500">
          <Lock className="h-3 w-3" />
          Only an administrator can change who counts as a broker.
        </span>
      </RosterBox>
    );
  }

  return (
    <RosterBox>
      <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-200">
          <RosterIcon />
          Who is a broker
        </h3>
        <span className="text-xs text-gray-500">
          {roster.isSeed
            ? 'Nobody classified yet, so everyone is listed as a broker'
            : `${num(unclassified)} of ${num(people.length)} not yet classified`}
        </span>
      </div>

      <p className="mb-3 text-xs text-gray-500">
        This changes how the Pipeline tab arranges people. It never changes a figure: deals owned by
        support staff and by acquired books stay in every company total exactly as they are now.
      </p>

      {roster.error && <p className="mb-3 text-sm text-red-400">{roster.error}</p>}
      {problem && <p className="mb-3 text-sm text-red-400">{problem}</p>}

      {roster.loading && !roster.ready ? (
        <p className="flex items-center gap-2 text-sm text-gray-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading the broker list
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-2">
            {people.map((person) => {
              const key = adviserKeyOf(person);
              const entry = roster.byKey.get(matchAdviserKey(key));
              const busy = roster.saving === key;
              const value =
                entry === undefined
                  ? 'unclassified'
                  : entry.disposition === 'group'
                    ? `group:${entry.groupKey}`
                    : entry.disposition;

              return (
                <div
                  key={key}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-700/70 bg-gray-900/45 px-3 py-2.5 transition-colors hover:bg-blue-500/[0.06]"
                >
                  <div className="min-w-0">
                    <div className="truncate text-[0.8125rem] text-gray-200">
                      {person.adviserName}
                    </div>
                    <div className="text-[0.6875rem] text-gray-500">
                      {num(person.totalCount)} deals in the export, {num(person.inFlightCount)} in
                      flight
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <select
                      value={value}
                      disabled={busy}
                      aria-label={`How to treat ${person.adviserName}`}
                      onChange={(e) => {
                        const next = e.target.value;
                        if (next.startsWith('group:')) {
                          void apply(person, 'group', next.slice('group:'.length));
                        } else {
                          void apply(person, next as RosterDisposition | 'unclassified');
                        }
                      }}
                      className="rounded-lg border border-gray-600 bg-gray-900 px-2 py-1.5 text-xs text-gray-200 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
                    >
                      <option value="unclassified">
                        Not classified (listed as {roster.defaultDisposition})
                      </option>
                      <option value="broker">Broker</option>
                      <option value="support">Support or admin</option>
                      {roster.groups.map((group) => (
                        <option key={group.key} value={`group:${group.key}`}>
                          Book: {group.label}
                        </option>
                      ))}
                    </select>
                    {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
                    {!busy && saved[key] && <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 border-t border-gray-700/70 pt-3">
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
              Acquired books
            </h4>
            {/* A book someone bought: its brokers are real and their deals matter, but they are
                not the practice's own advisers, so they roll up to one row that expands. */}
            {roster.groups.length === 0 ? (
              <p className="mb-2 text-xs text-gray-500">
                None yet. Add one to roll a purchased book up under a single heading.
              </p>
            ) : (
              <div className="mb-2 flex flex-wrap gap-2">
                {roster.groups.map((group) => (
                  <span
                    key={group.key}
                    className="flex items-center gap-1.5 rounded-full border border-gray-600 bg-gray-900/60 px-2.5 py-1 text-xs text-gray-300"
                  >
                    {group.label}
                    <button
                      type="button"
                      onClick={() => void removeBook(group.key)}
                      aria-label={`Remove ${group.label}`}
                      title="Removing a book returns its brokers to unclassified. No deal is affected."
                      className="text-gray-500 hover:text-red-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <input
                value={newBook}
                onChange={(e) => setNewBook(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void addBook();
                  }
                }}
                placeholder="Name of a book you acquired"
                aria-label="Name of a book you acquired"
                maxLength={120}
                className="min-w-0 flex-1 rounded-lg border border-gray-600 bg-gray-900 px-2.5 py-1.5 text-xs text-gray-200 placeholder:text-gray-600 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
              />
              <button
                type="button"
                onClick={() => void addBook()}
                disabled={newBook.trim() === ''}
                className="rounded-lg border border-gray-600 bg-gray-900 px-3 py-1.5 text-xs text-gray-200 hover:bg-gray-700/50 disabled:opacity-40 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
              >
                Add book
              </button>
            </div>
          </div>

          <div className="mt-4 border-t border-gray-700/70 pt-3">
            <label className="flex flex-wrap items-center gap-2 text-xs text-gray-400">
              Someone new, who has not been classified yet, is listed as
              <select
                value={roster.defaultDisposition}
                disabled={roster.saving === '__default__'}
                onChange={(e) => {
                  void roster
                    .setDefaultDisposition(e.target.value as 'broker' | 'support')
                    .then((outcome) => {
                      if (outcome.ok) onSaved();
                      else setProblem(outcome.message ?? 'That could not be saved.');
                    });
                }}
                className="rounded-lg border border-gray-600 bg-gray-900 px-2 py-1.5 text-xs text-gray-200 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
              >
                <option value="broker">a broker</option>
                <option value="support">support</option>
              </select>
            </label>
            {/* Broker is the default because it is what the page did before this existed, so a
                new broker shows up the week they write their first deal instead of waiting for
                somebody to remember them. The cost is that a new support hire shows up too,
                which is what the unclassified count above is for. */}
            <p className="mt-1.5 text-[0.6875rem] text-gray-500">
              Leaving this on &ldquo;a broker&rdquo; means a new broker appears as soon as they
              write a deal. New support staff will appear too, until you classify them.
            </p>
          </div>
        </>
      )}
    </RosterBox>
  );
};

export default BrokerRosterPanel;
